import { useEffect, useState } from 'react';
import { Pencil, Plus, Trash2, X, Check, Link2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import {
  type TickerMapping,
  deleteMapping,
  listMappings,
  upsertMapping,
} from '@/lib/tickerMappings';

interface Props {
  /** Pre-fills the editor when the user clicks "Map this symbol" on a failed row. */
  prefill?: { symbol?: string; isin?: string; resolved?: string; notes?: string } | null;
  onPrefillConsumed?: () => void;
  /** Called whenever mappings change so the parent can re-run with fresh overrides. */
  onChange?: () => void;
}

const empty = { id: '', input_symbol: '', input_isin: '', resolved_symbol: '', notes: '' };

export function TickerMappingsManager({ prefill, onPrefillConsumed, onChange }: Props) {
  const { toast } = useToast();
  const [rows, setRows] = useState<TickerMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({ ...empty });
  const [editing, setEditing] = useState(false);
  // Tracks whether the editor was opened by an auto-suggestion from the 404 retry diagnosis.
  const [suggestion, setSuggestion] = useState<{ resolved: string; reason: string } | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      setRows(await listMappings());
    } catch (e) {
      toast({ title: 'Failed to load mappings', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    if (prefill && (prefill.symbol || prefill.isin || prefill.resolved)) {
      setDraft({
        id: '',
        input_symbol: prefill.symbol || '',
        input_isin: prefill.isin || '',
        resolved_symbol: prefill.resolved || '',
        notes: prefill.notes || '',
      });
      setEditing(true);
      // Show the "suggested from 404 retry" banner whenever the parent prefilled
      // a resolved Yahoo symbol (the one-click "Map → {bestTried}" action).
      setSuggestion(
        prefill.resolved
          ? { resolved: prefill.resolved, reason: prefill.notes || 'Auto-suggested from the 404 retry diagnosis.' }
          : null,
      );
      onPrefillConsumed?.();
    }
  }, [prefill, onPrefillConsumed]);

  const startNew = () => { setDraft({ ...empty }); setSuggestion(null); setEditing(true); };
  const startEdit = (m: TickerMapping) => {
    setDraft({
      id: m.id,
      input_symbol: m.input_symbol || '',
      input_isin: m.input_isin || '',
      resolved_symbol: m.resolved_symbol,
      notes: m.notes || '',
    });
    setSuggestion(null);
    setEditing(true);
  };
  const cancel = () => { setEditing(false); setDraft({ ...empty }); setSuggestion(null); };

  const save = async () => {
    try {
      await upsertMapping({
        id: draft.id || undefined,
        input_symbol: draft.input_symbol || null,
        input_isin: draft.input_isin || null,
        resolved_symbol: draft.resolved_symbol,
        notes: draft.notes || null,
      });
      toast({ title: 'Mapping saved' });
      cancel();
      await refresh();
      onChange?.();
    } catch (e) {
      toast({ title: 'Failed to save mapping', description: (e as Error).message, variant: 'destructive' });
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteMapping(id);
      toast({ title: 'Mapping deleted' });
      await refresh();
      onChange?.();
    } catch (e) {
      toast({ title: 'Failed to delete', description: (e as Error).message, variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Link2 className="h-4 w-4" />
          Manual ticker mappings ({rows.length})
        </CardTitle>
        {!editing && (
          <Button size="sm" variant="outline" onClick={startNew}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add mapping
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {editing && (
          <div className="space-y-2">
            {suggestion && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5">
                <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="text-[11px] leading-snug">
                  <div className="font-semibold text-amber-700 dark:text-amber-300">
                    Suggested from 404 retry
                  </div>
                  <div className="text-foreground/90">
                    Yahoo returned 404 for every candidate, so the most-specific tried ticker —{' '}
                    <code className="font-mono px-1 py-0.5 rounded bg-background border border-border">
                      {suggestion.resolved}
                    </code>{' '}
                    — was prefilled below. Verify it's the correct instrument, then save to lock it in.
                  </div>
                  <div className="text-muted-foreground mt-0.5">{suggestion.reason}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setSuggestion(null)}
                  className="ml-auto text-muted-foreground hover:text-foreground"
                  aria-label="Dismiss suggestion note"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <div className="grid gap-2 rounded-md border bg-muted/30 p-3 md:grid-cols-[1fr_1fr_1fr_1.2fr_auto]">
            <Input
              placeholder="Input symbol (e.g. NESN)"
              value={draft.input_symbol}
              onChange={(e) => setDraft({ ...draft, input_symbol: e.target.value })}
            />
            <Input
              placeholder="Input ISIN (e.g. CH0038863350)"
              value={draft.input_isin}
              onChange={(e) => setDraft({ ...draft, input_isin: e.target.value })}
            />
            <Input
              placeholder="Yahoo symbol (e.g. NESN.SW)"
              value={draft.resolved_symbol}
              onChange={(e) => setDraft({ ...draft, resolved_symbol: e.target.value })}
            />
            <Input
              placeholder="Notes (optional)"
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
            <div className="flex gap-1">
              <Button size="sm" onClick={save}><Check className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="ghost" onClick={cancel}><X className="h-3.5 w-3.5" /></Button>
            </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-xs text-muted-foreground py-2">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-muted-foreground py-2">
            No mappings yet. Add one to force a specific Yahoo symbol for an input symbol or ISIN.
          </div>
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="h-8 text-xs">Input symbol</TableHead>
                  <TableHead className="h-8 text-xs">Input ISIN</TableHead>
                  <TableHead className="h-8 text-xs">→ Yahoo symbol</TableHead>
                  <TableHead className="h-8 text-xs">Notes</TableHead>
                  <TableHead className="h-8 text-xs w-[90px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="text-xs font-mono">{m.input_symbol || '—'}</TableCell>
                    <TableCell className="text-xs font-mono">{m.input_isin || '—'}</TableCell>
                    <TableCell className="text-xs font-mono font-semibold">{m.resolved_symbol}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{m.notes || ''}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => startEdit(m)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => remove(m.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
